'use client';

/**
 * Bold Navigation Component for The Audax Group
 * Features shopping cart, smooth animations, and brand/consumer pathways
 */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ShoppingCart, Menu, X, Sparkles, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { useCartStore } from '@/lib/store';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function Navigation() {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { getTotalItems, openCart } = useCartStore();
  const [totalItems, setTotalItems] = useState(0);

  // Update cart count
  useEffect(() => {
    setTotalItems(getTotalItems());
  }, [getTotalItems]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const brandLinks = [
    { href: '/activations', label: 'Our Activations' },
    { href: '/partnerships', label: 'Partner With Us' },
    { href: '/about', label: 'About' },
  ];

  const consumerLinks = [
    { href: '/events', label: 'Events' },
    { href: '/shop', label: 'Shop' },
    { href: '/about', label: 'About' },
  ];

  const isActive = (href: string) => pathname === href;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background/95 backdrop-blur-md shadow-md'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-electric rounded-lg opacity-0 group-hover:opacity-100 blur transition duration-300" />
                <div className="relative bg-background px-4 py-2 rounded-lg border-2 border-primary">
                  <span className="text-2xl font-black text-gradient-electric">
                    AUDAX
                  </span>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {/* Brand Links */}
              <div className="flex items-center space-x-1 mr-4">
                <Building2 className="w-4 h-4 text-primary mr-2" />
                {brandLinks.map((link) => (
                  <Link key={link.href} href={link.href}>
                    <Button
                      variant={isActive(link.href) ? 'default' : 'ghost'}
                      className={`relative group ${
                        isActive(link.href)
                          ? 'bg-primary text-primary-foreground'
                          : ''
                      }`}
                    >
                      {link.label}
                      {!isActive(link.href) && (
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-electric transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                      )}
                    </Button>
                  </Link>
                ))}
              </div>

              <div className="w-px h-8 bg-border mx-2" />

              {/* Consumer Links */}
              <div className="flex items-center space-x-1 ml-4">
                <Sparkles className="w-4 h-4 text-secondary mr-2" />
                {consumerLinks.map((link) => (
                  <Link key={link.href} href={link.href}>
                    <Button
                      variant={isActive(link.href) ? 'default' : 'ghost'}
                      className={`relative group ${
                        isActive(link.href)
                          ? 'bg-secondary text-secondary-foreground'
                          : ''
                      }`}
                    >
                      {link.label}
                      {!isActive(link.href) && (
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-sunset transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                      )}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>

            {/* Cart & Mobile Menu */}
            <div className="flex items-center space-x-4">
              {/* Shopping Cart */}
              <Button
                variant="ghost"
                size="icon"
                onClick={openCart}
                className="relative group"
              >
                <ShoppingCart className="w-5 h-5" />
                {totalItems > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-secondary text-secondary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-neon"
                  >
                    {totalItems}
                  </motion.span>
                )}
              </Button>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 lg:hidden pt-20"
          >
            <div
              className="absolute inset-0 bg-background/98 backdrop-blur-lg"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="relative container mx-auto px-4 py-8 space-y-8">
              {/* Brand Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-primary">For Brands</h3>
                </div>
                <div className="space-y-2">
                  {brandLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button
                        variant={isActive(link.href) ? 'default' : 'ghost'}
                        className="w-full justify-start text-lg"
                      >
                        {link.label}
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="h-px bg-border" />

              {/* Consumer Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Sparkles className="w-5 h-5 text-secondary" />
                  <h3 className="text-lg font-bold text-secondary">
                    For Fans
                  </h3>
                </div>
                <div className="space-y-2">
                  {consumerLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button
                        variant={isActive(link.href) ? 'secondary' : 'ghost'}
                        className="w-full justify-start text-lg"
                      >
                        {link.label}
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed nav */}
      <div className="h-20" />
    </>
  );
}
